import { useMemo } from "react";
import { CardHeaderTag } from "components/shared/Tags/StatusTags";
import strings from "./strings";
import { MapPin, Download } from "lucide-react";
import { STATUS_BEGIN } from "../../../../utils/constants/others";
import { Loader } from "../../Common/Loader";
import { PurchaseOrderPageTabs, PurchaseOrderErrorState, PurchaseOrderEmptyState } from "./PurchaseOrderListElements";
import TableWithFilters from "components/shared/TableWithFilters/TableWithFilters";
import PurchaseOrderFilters from "./PurchaseOrderFilters";
import SearchBarRed from "components/shared/SearchBar/SearchBarRed";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import { DesktopPage, Title, TitleRow } from "components/core/Common/DesktopHelpers/DesktopHelpers";
import {
  PoIdLink,
  DeliveryAtCell,
  DownloadButton,
  CellText,
} from "./styles.purchaseOrderListDesktop";

export default function PurchaseOrderListDesktop({
  activePage,
  pageNumber,
  purchaseOrderTableRowData,
  hasNextPage,
  getPurchaseOrderDetailsStatus,
  handlePageChange,
  goToPage,
  handleOpen,
  handleDownload,
  activePageConfig,
  emptyStateDestination,
  isInitialLoading,
  hasError,
  fetchPurchaseOrders,
  selectedStatuses,
  toggleStatus,
  selectedDeliveryDate,
  setSelectedDeliveryDate,
  selectedWarehouses,
  toggleWarehouse,
  applyFilters,
  clearFilters,
  searchQuery,
  setSearchQuery,
  isSearching,
}) {
  const columns = useMemo(
    () => [
      {
        id: "po_id",
        header: "PO ID",
        meta: { width: 200 },
        cell: (info) => (
          <PoIdLink>
            {info.row.original.poNumber}
          </PoIdLink>
        ),
      },
      {
        id: "skus",
        header: "SKUs",
        meta: { width: 120 },
        cell: (info) => <CellText>{`${info.row.original.skuCount} SKUs`}</CellText>,
      },
      {
        id: "net_weight",
        header: "Net weight",
        meta: { width: 120 },
        cell: (info) => <CellText>{info.row.original.netWeight}</CellText>,
      },
      {
        id: "deliver_by",
        header: "Deliver by",
        meta: { width: 160 },
        cell: (info) => <CellText>{info.row.original.deliverBy}</CellText>,
      },
      {
        id: "po_amount",
        header: "PO amount",
        meta: { width: 140 },
        cell: (info) => <CellText>{info.row.original.poAmount}</CellText>,
      },
      {
        id: "delivery_at",
        header: "Delivery at",
        meta: { width: 210 },
        cell: (info) => (
          <DeliveryAtCell>
            <MapPin size={16} />
            <CellText>{info.row.original.deliveryAt}</CellText>
          </DeliveryAtCell>
        ),
      },
      {
        id: "status",
        header: "Status",
        meta: { width: 200 },
        cell: (info) => (
          <CardHeaderTag
            text={info.row.original.statusLabel}
            bgColor={info.row.original.statusBgColor}
            textColor={info.row.original.statusTextColor}
            allSidesRounded
          />
        ),
      },
      {
        id: "action",
        header: "Action",
        meta: { width: 134, sticky: "right" },
        cell: (info) => {
          const hasDoc = info.row.original.hasDocument;
          return (
            <DownloadButton
              type="button"
              onClick={(e) => { e.stopPropagation(); hasDoc && handleDownload(info.row.original._raw); }}
              $hasDoc={hasDoc}
            >
              <Download size={20} color={hasDoc ? "#E23744" : "#BDBDBD"} />
            </DownloadButton>
          );
        },
      },
    ],
    [handleOpen, handleDownload]
  );

  return (
    <DesktopPage>
      <Breadcrumb items={[{ label: "Home" }, { label: "Purchase orders" }]} />
      <TitleRow>
        <Title>Purchase orders</Title>
        <SearchBarRed
          placeholder={strings.SEARCH_PLACEHOLDER}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </TitleRow>
      <PurchaseOrderPageTabs activePage={activePage} onChange={handlePageChange} />
      {isInitialLoading || getPurchaseOrderDetailsStatus === STATUS_BEGIN ? (
        <Loader />
      ) : hasError ? (
        <PurchaseOrderErrorState onRetry={() => fetchPurchaseOrders(1, true)} />
      ) : purchaseOrderTableRowData.length === 0 && !isSearching && selectedStatuses.length === 0 && !selectedDeliveryDate && selectedWarehouses.length === 0 ? (
        <PurchaseOrderEmptyState
          pageConfig={activePageConfig}
          isSearch={isSearching}
          nextPage={emptyStateDestination}
          onOpenPage={handlePageChange}
        />
      ) : (
        <TableWithFilters
          filters={
            <PurchaseOrderFilters
              selectedStatuses={selectedStatuses}
              onToggleStatus={toggleStatus}
              selectedDeliveryDate={selectedDeliveryDate}
              onDeliveryDateChange={setSelectedDeliveryDate}
              selectedWarehouses={selectedWarehouses}
              onToggleWarehouse={toggleWarehouse}
              onApply={applyFilters}
              onClear={clearFilters}
            />
          }
          columns={columns}
          data={purchaseOrderTableRowData}
          getRowId={(row) => `${row.id}-${row.poStatus}`}
          isLoading={getPurchaseOrderDetailsStatus === STATUS_BEGIN}
          emptyMessage="No purchase orders found"
          page={pageNumber}
          hasNextPage={hasNextPage}
          onPageChange={goToPage}
          onRowClick={(row) => handleOpen(row._raw, row.poStatus)}
        />
      )}
    </DesktopPage>
  );
}
